import styled from 'styled-components'

export type ColorType = 'neutral' | 'white' | 'primary'

export type VariantType = 'solid' | 'outline' | 'ghost' | 'faded'

export type SizeString = 'small' | 'medium' | 'large' | 'xlarge'

export type SizeObject = { default: SizeString; mobile?: SizeString; tablet?: SizeString; desktop?: SizeString }
export type OptionalSizeObject = {
  default?: SizeString
  mobile?: SizeString
  tablet?: SizeString
  desktop?: SizeString
}

export type SizeType = SizeString | OptionalSizeObject

export const BorderColor: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Border-Neutral, #BBC1D3)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'rgba(0, 0, 0, 0.2)',
  },
  outline: {
    neutral: 'var(--Border-Neutral, #BBC1D3)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, #4D4BE7)',
  },
  ghost: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255,255,255,0)',
    primary: 'rgba(255,255,255,0)',
  },
  faded: {
    neutral: 'var(--Background-Neutral, #DFE2EA)',
    white: 'rgba(255, 255, 255, 0.28)',
    primary: 'var(--Background-Primary-faded, #EDECFD)',
  },
}

export const BackgroundColor: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Background-Neutral, #DFE2EA)',
    white: 'var(--Persistent-White, #FFF)',
    primary:
      // eslint-disable-next-line max-len
      'linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%), var(--Foreground-Primary, #4D4BE7)',
  },
  outline: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255, 255, 255, 0)',
    primary: 'rgba(255, 255, 255, 0)',
  },
  ghost: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255,255,255,0)',
    primary: 'rgba(255,255,255,0)',
  },
  faded: {
    neutral: 'var(--Background-Elevation-base, #FFF)',
    white: 'rgba(255, 255, 255, 0.28)',
    primary: 'var(--Background-Primary-faded, #EDECFD)',
  },
}

export const Color: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-Black, #000)',
    primary: 'var(--On-background-Primary, #FFF)',
  },
  outline: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
  },
  ghost: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
  },
  faded: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-Black, #000)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
  },
}

const HEIGHT: { [key in SizeString]: number } = {
  small: 28,
  medium: 36,
  large: 48,
  xlarge: 52,
}

const FONT_SIZE: { [key in SizeString]: number } = {
  small: 14,
  medium: 14,
  large: 16,
  xlarge: 18,
}

export const HTMLButton = styled.button<{ variant: VariantType; color: ColorType; size: SizeObject }>`
  background-blend-mode: soft-light, normal;
  background: ${({ variant, color }) => BackgroundColor[variant][color]};
  outline: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  border-radius: 6px;
  border: 1px solid ${({ variant, color }) => BorderColor[variant][color]};

  padding: 4px 8px;
  box-sizing: border-box;

  color: ${({ variant, color }) => Color[variant][color]};

  overflow: hidden;

  position: relative;

  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  svg {
    path {
      fill: ${({ variant, color }) => Color[variant][color]};
    }
  }

  height: ${({ size }) => HEIGHT[size.desktop || size.default]}px;

  /* Body 3/Medium */
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ size }) => FONT_SIZE[size.desktop || size.default]}px;
  font-style: normal;
  font-weight: 400;

  @media (max-width: ${({ theme }) => theme.responsive.tablet.valueString}) {
    height: ${({ size }) => HEIGHT[size.tablet || size.default]}px;

    font-size: ${({ size }) => FONT_SIZE[size.tablet || size.default]}px;
  }

  @media (max-width: ${({ theme }) => theme.responsive.mobile.valueString}) {
    height: ${({ size }) => HEIGHT[size.mobile || size.default]}px;

    font-size: ${({ size }) => FONT_SIZE[size.mobile || size.default]}px;
  }
`

export const ChildrenContainer = styled.div<{ isLoading?: boolean }>`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  line-height: 16px;
`

export const LoaderContainer = styled.div`
  position: absolute;

  cursor: default;

  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`
